import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactChild
} from "react"
import { noop } from "lib/helpers"

export type AlertDetail = {
  id: number
  message: string
  type: `success` | `warning` | `error` | `info`
  dismissed: boolean
}

export const AlertsContext = createContext<{
  alerts: AlertDetail[]
  dismissAlert: (id: number) => void
  dismissAllAlerts: () => void
  deleteAlert: (id: number) => void
  dispatchAlert: (
    details: Omit<AlertDetail, `id` | `dismissed`>,
    timeout?: number
  ) => number
}>({
  alerts: [],
  dismissAlert: noop,
  dismissAllAlerts: noop,
  deleteAlert: noop,
  dispatchAlert: () => 0
})

let lastUsedId = 0

export const AlertsProvider = ({ children }: { children: ReactChild }) => {
  const [alerts, setAlerts] = useState<AlertDetail[]>([])
  const timeouts = useRef<NodeJS.Timeout[]>([])

  const dismissAlert = useCallback(
    (id: number) =>
      setAlerts(currentAlerts =>
        currentAlerts.map(alert =>
          alert.id === id ? { ...alert, dismissed: true } : alert
        )
      ),
    []
  )

  const deleteAlert = useCallback(
    (id: number) =>
      setAlerts(currentAlerts =>
        currentAlerts.filter(alert => alert.id !== id)
      ),
    []
  )

  const dismissAllAlerts = useCallback(
    () =>
      setAlerts(currentAlerts =>
        currentAlerts.map(alert => ({
          ...alert,
          dismissed: true
        }))
      ),
    []
  )

  const dispatchAlert = useCallback(
    (
      { message, type }: Omit<AlertDetail, `id` | `dismissed`>,
      dismissAfterMs = 5000
    ) => {
      lastUsedId += 1
      const newAlert: AlertDetail = {
        id: lastUsedId,
        message,
        type,
        dismissed: false
      }
      setAlerts(currentAlerts => [newAlert, ...currentAlerts])

      const timeout = setTimeout(() => {
        dismissAlert(newAlert.id)
      }, dismissAfterMs)

      timeouts.current.push(timeout)

      return newAlert.id
    },
    [dismissAlert]
  )

  // This is to prevent some cleanup errors where the component unmounts before
  // a setTimeout to dismiss a alert has fired
  useEffect(() => {
    while (timeouts.current.length) {
      clearTimeout(timeouts.current.shift())
    }
  }, [])

  const value = useMemo(
    () => ({
      alerts,
      dispatchAlert,
      dismissAlert,
      deleteAlert,
      dismissAllAlerts
    }),
    [alerts, dispatchAlert, dismissAlert, deleteAlert, dismissAllAlerts]
  )

  return (
    <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>
  )
}

export const useAlertsContext = () => {
  const context = useContext(AlertsContext)
  if (context === undefined) {
    throw new Error(`useAlertsContext must be used within an AlertsProvider`)
  }

  return context
}
